import { FC } from 'react'
import { useAuth } from '../../context/auth-context'
import { useDateTime } from '../../shared/useDateTime'
import { useAmountAndCurrency } from '../../shared/useAmountAndCurrency'
import { useTranslation } from 'react-i18next'
import PerText from '../../components/custom_components/Text'
import {
  EmployeeDisplayTinyInfo,
  KeyResultDto,
  ObjectiveDto,
  Currency,
  MeetingDashboardFragment,
  MeetingStatus,
  MeetingHrFeedbackResult,
  CurrencyConversionDto,
  SkillEmployeeStatusDto,
} from '../../types'
import { Table, Tooltip } from 'antd'
import { PerEmpty } from '../../components/custom_components/Empty'
import { tableActionButtons } from '../../components/custom_components/TableActionButtons/tableActionButtons.uiprops'
import { RequestMeetingInviteButton } from '../meeting/invite/RequestMeetingInviteButton'
import { ObjectiveColumnKey } from '../appraisal/okr/lists/objectiveColumnKey'
import { EmployeeTargetsComponentActionButtons } from './EmployeeTargetsComponentActionButtons'
import PerFlexRow from '../../components/custom_components/FlexRow'
import { retrieveCurrencyConversionRate } from '../../shared/currencyConversionUtil'
import { AppraisalInviteButton } from '../meeting/invite/AppraisalInviteButton'

interface EmployeeTargetsComponentProps {
  handleObjectiveClicked: (isManagerPreparation: boolean, objective: ObjectiveDto) => void
  objectives: ObjectiveDto[]
  monetary?: boolean
  publicProfile?: boolean
  employee?: EmployeeDisplayTinyInfo
  employeeIsEditable?: boolean
  resignedView?: boolean
  currency?: Currency
  plannedMeeting?: MeetingDashboardFragment
  currencyConversionData?: CurrencyConversionDto[]
  employeeSkillStatus: SkillEmployeeStatusDto | null
  view: 'ResignedEmployeeProfilePage' | 'OwnGrowthPage' | 'PublicProfilePage'
}

export const EmployeeTargetsComponent: FC<EmployeeTargetsComponentProps> = ({
  objectives,
  handleObjectiveClicked,
  employee,
  monetary = false,
  publicProfile = false,
  employeeIsEditable,
  resignedView,
  currency, // Currency of current contract
  plannedMeeting,
  currencyConversionData = [],
  employeeSkillStatus,
  view,
}) => {
  const { user } = useAuth()
  const { formatDateShort } = useDateTime(user)
  const { formatCurrency, calculateAndFormatNewTargetAmount } = useAmountAndCurrency(user.locale)
  const [t] = useTranslation()

  const { tableActionButtonsRowWidth } = tableActionButtons()

  const employeeId = employee ? employee.id : user.employeeId

  const columns = [
    {
      key: ObjectiveColumnKey.Title,
      title: monetary ? t('okr.monetary.title') : t('okr.development.title'),
      dataIndex: 'title',
      width: monetary ? '200px' : '300px',
      render: (title: string) => <PerText fontWeight="bold">{title}</PerText>,
    },
    {
      key: ObjectiveColumnKey.MonetaryBonus,
      title: t('okr.objective.monetary.bonus'),
      className: 'minWidth-column-m textAlign-right',
      render: (objective: ObjectiveDto) => {
        const targetConversionRate: number | undefined =
          !!objective.currency && !!currency && objective.currency !== currency
            ? retrieveCurrencyConversionRate(objective.currency, currency, currencyConversionData)
            : undefined
        return (
          <>
            {objective.targetAmount !== undefined && objective.targetAmount !== null && (
              <PerFlexRow direction="column">
                <Tooltip
                  title={
                    objective.currency !== currency
                      ? targetConversionRate !== undefined
                        ? t('currencyConversion.tooltips.tableWarning')
                        : t('currencyConversion.tooltips.tableConversionWarning', {
                            startingCurrency: objective.currency,
                            targetCurrency: currency,
                          })
                      : undefined
                  }
                  placement="right"
                >
                  <PerText>{formatCurrency(objective.targetAmount, objective.currency!)}</PerText>
                  {objective.currency !== currency && (
                    <PerText fontSize="xs" mt="xxs">
                      {calculateAndFormatNewTargetAmount(objective.targetAmount, currency!, targetConversionRate)}
                    </PerText>
                  )}
                </Tooltip>
              </PerFlexRow>
            )}
          </>
        )
      },
    },
    {
      key: ObjectiveColumnKey.KeyResults,
      title: t('okr.objective.keyResults.tasks'),
      dataIndex: 'keyResults',
      className: 'textAlign-left minWidth-column-xl',
      render: (keyResults: KeyResultDto[]) => {
        const keyResultsLength = keyResults.length
        const keyResultsCompletedLength = keyResults.filter((kr) => kr.completed).length
        return (
          <PerText fontSize="s" color="secondary">
            <PerText
              fontSize="xl"
              display="inline"
              color={keyResultsLength === keyResultsCompletedLength ? 'success' : keyResultsCompletedLength === 0 ? 'error' : 'warning'}
            >
              {keyResultsCompletedLength}
            </PerText>
            {' / ' + keyResultsLength}
          </PerText>
        )
      },
    },
    {
      key: ObjectiveColumnKey.Due,
      title: t('okr.objective.due'),
      dataIndex: 'due',
      className: 'minWidth-column-m',
      render: (due: string) => <PerText>{formatDateShort(due, true)}</PerText>,
    },
    {
      key: ObjectiveColumnKey.Actions,
      title: t('okr.objective.actions'),
      className: 'textAlign-left',
      width: tableActionButtonsRowWidth(2),
      render: (objective: ObjectiveDto) => {
        return (
          <EmployeeTargetsComponentActionButtons
            handleObjectiveClicked={handleObjectiveClicked}
            objective={objective}
            employeeId={employeeId}
          />
        )
      },
    },
  ]

  const filterMonetaryCol = (column: { key: string }) => monetary || column.key !== ObjectiveColumnKey.MonetaryBonus

  const emptyTableText = (publicMoneyText, publicDevText, moneyText, devText, resignedViewText) => {
    if (resignedView) {
      return resignedViewText
    } else if (publicProfile) {
      return monetary ? publicMoneyText : publicDevText
    } else {
      return monetary ? moneyText : devText
    }
  }

  const emptyTableActionButton = () => {
    if (publicProfile && employee && employeeIsEditable) {
      return <AppraisalInviteButton employeeId={employee.id} employeeSkillStatus={employeeSkillStatus} />
    } else if (!employee || (!!employee && user.employeeId === employee.id)) {
      return <RequestMeetingInviteButton context="OwnGrowth" />
    } else return ''
  }

  const renderPrivateSubheading = () => {
    return !!plannedMeeting ? '' : t('targetView.emptyTable.infoText')
  }

  const renderPublicSubheading = () => {
    if (!plannedMeeting && employeeIsEditable) {
      return t('targetView.emptyTable.public.infoText')
    }
    if (plannedMeeting) {
      if ([MeetingStatus.Draft, MeetingStatus.Prepared].includes(plannedMeeting.status)) {
        const dateOfMeeting = formatDateShort(plannedMeeting.displayDate)
        const employeeFullName = plannedMeeting.employee
          ? `${plannedMeeting.employee.chosenFirstName || plannedMeeting.employee.legalFirstName} ${
              plannedMeeting.employee.chosenLastName || plannedMeeting.employee.legalLastName
            }`
          : undefined
        return plannedMeeting.reviewer && plannedMeeting.reviewer.id === user.employeeId
          ? t('ownGrowthView.meetingStatus.manager.plannedMeetingWithYou', { employeeFullName, dateOfMeeting })
          : t('ownGrowthView.meetingStatus.manager.plannedMeeting', {
              employeeFullName,
              reviewerFullName: plannedMeeting.reviewer
                ? `${plannedMeeting.reviewer.firstName} ${plannedMeeting.reviewer.lastName}`
                : undefined,
              dateOfMeeting,
            })
      } else if (
        plannedMeeting.status === MeetingStatus.PendingConfirmation ||
        (plannedMeeting.status === MeetingStatus.DisputedConfirmed &&
          plannedMeeting.appraisal &&
          plannedMeeting.appraisal.hrResult === MeetingHrFeedbackResult.NoResult)
      ) {
        const employeeFullName = plannedMeeting.employee
          ? `${plannedMeeting.employee.chosenFirstName || plannedMeeting.employee.legalFirstName} ${
              plannedMeeting.employee.chosenLastName || plannedMeeting.employee.legalLastName
            }`
          : undefined
        return t('ownGrowthView.meetingStatus.manager.waitingForConfirmation', { employeeFullName })
      }
      return ''
    }
    return ''
  }

  return (
    <Table
      className={'small light ' + (!!plannedMeeting && view === 'OwnGrowthPage' ? 'messageBoxTargets' : 'targets')}
      dataSource={objectives}
      rowKey={'globalId'}
      columns={columns.filter((c) => filterMonetaryCol(c))}
      pagination={false}
      scroll={{ y: false }}
      locale={{
        emptyText: (
          <PerEmpty
            icon="targetsNo_stroke"
            heading={emptyTableText(
              t('targetView.emptyTable.public.monetary'),
              t('targetView.emptyTable.public.development'),
              t('targetView.emptyTable.monetary'),
              t('targetView.emptyTable.development'),
              t('targetView.emptyTable.resignedView', {
                name: employee ? employee.firstName : '',
                targetType: monetary ? 'monetary' : 'development',
              }),
            )}
            info={resignedView ? '' : publicProfile ? renderPublicSubheading() : renderPrivateSubheading()}
            button={!resignedView && !plannedMeeting && emptyTableActionButton()}
          />
        ),
      }}
    />
  )
}
